import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Plus, Edit, Trash2, Eye, EyeOff } from "lucide-react";
import { Tables } from "@/integrations/supabase/types";
import WorldCategoryForm from "./WorldCategoryForm";
import WorldItemForm from "./WorldItemForm";
import { sanitizeHtml } from "@/utils/sanitization";

type WorldCategory = Tables<"world_categories">;
type WorldItem = Tables<"world_items">;

const WorldContentManager = () => {
  const [categories, setCategories] = useState<WorldCategory[]>([]);
  const [items, setItems] = useState<WorldItem[]>([]);
  const [filteredItems, setFilteredItems] = useState<WorldItem[]>([]);
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState<string>("all");
  const [loading, setLoading] = useState(true);
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [showItemForm, setShowItemForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState<WorldCategory | null>(null);
  const [editingItem, setEditingItem] = useState<WorldItem | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    // Filter items based on selected category
    if (selectedCategoryFilter === "all") {
      setFilteredItems(items);
    } else {
      setFilteredItems(items.filter(item => item.category_id === selectedCategoryFilter));
    }
  }, [items, selectedCategoryFilter]);

  const fetchData = async () => {
    try {
      const [categoriesResult, itemsResult] = await Promise.all([
        supabase.from("world_categories").select("*").order("display_order"),
        supabase.from("world_items").select("*").order("display_order")
      ]);

      if (categoriesResult.error) throw categoriesResult.error;
      if (itemsResult.error) throw itemsResult.error;

      setCategories(categoriesResult.data || []);
      setItems(itemsResult.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast({
        title: "Error",
        description: "Failed to fetch world content",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCategorySubmit = async (data: Omit<WorldCategory, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      if (editingCategory) {
        const { error } = await supabase
          .from("world_categories")
          .update(data)
          .eq("id", editingCategory.id);

        if (error) throw error;
        toast({ title: "Category updated successfully" });
      } else {
        const { error } = await supabase
          .from("world_categories")
          .insert(data);

        if (error) throw error;
        toast({ title: "Category created successfully" });
      }

      await fetchData();
      setShowCategoryForm(false);
      setEditingCategory(null);
    } catch (error) {
      console.error("Error saving category:", error);
      toast({
        title: "Error",
        description: "Failed to save category",
        variant: "destructive"
      });
    }
  };

  const toggleCategoryStatus = async (id: string, isActive: boolean) => {
    try {
      const { error } = await supabase
        .from("world_categories")
        .update({ is_active: !isActive })
        .eq("id", id);

      if (error) throw error;

      await fetchData();
      toast({
        title: "Success",
        description: `Category ${!isActive ? "activated" : "deactivated"} successfully`
      });
    } catch (error) {
      console.error("Error updating category:", error);
      toast({
        title: "Error",
        description: "Failed to update category status",
        variant: "destructive"
      });
    }
  };

  const toggleItemStatus = async (id: string, isActive: boolean) => {
    try {
      const { error } = await supabase
        .from("world_items")
        .update({ is_active: !isActive })
        .eq("id", id);

      if (error) throw error;

      await fetchData();
      toast({
        title: "Success",
        description: `Item ${!isActive ? "activated" : "deactivated"} successfully`
      });
    } catch (error) {
      console.error("Error updating item:", error);
      toast({
        title: "Error",
        description: "Failed to update item status",
        variant: "destructive"
      });
    }
  };

  const deleteCategory = async (id: string) => {
    if (!confirm("Are you sure you want to delete this category? This will also delete all items in this category.")) {
      return;
    }

    try {
      const { error } = await supabase
        .from("world_categories")
        .delete()
        .eq("id", id);

      if (error) throw error;

      await fetchData();
      toast({
        title: "Success",
        description: "Category deleted successfully"
      });
    } catch (error) {
      console.error("Error deleting category:", error);
      toast({
        title: "Error",
        description: "Failed to delete category",
        variant: "destructive"
      });
    }
  };

  const deleteItem = async (id: string) => {
    if (!confirm("Are you sure you want to delete this item?")) {
      return;
    }

    try {
      const { error } = await supabase
        .from("world_items")
        .delete()
        .eq("id", id);

      if (error) throw error;

      await fetchData();
      toast({
        title: "Success",
        description: "Item deleted successfully"
      });
    } catch (error) {
      console.error("Error deleting item:", error);
      toast({
        title: "Error",
        description: "Failed to delete item",
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  const handleEditItem = async (id: string) => {
    try {
      const { data, error } = await supabase
        .from("world_items")
        .select("*")
        .eq("id", id)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setEditingItem(data as WorldItem);
      } else {
        setEditingItem(null);
      }
      setShowItemForm(true);
    } catch (error) {
      console.error("Error loading item details:", error);
      toast({
        title: "Error",
        description: "Failed to load item details",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-fredoka font-bold text-tinyminds-purple">
          World Content Management
        </h2>
      </div>

      <Tabs defaultValue="categories" className="space-y-6">
        <TabsList>
          <TabsTrigger value="categories">Categories</TabsTrigger>
          <TabsTrigger value="items">World Items</TabsTrigger>
        </TabsList>

        <TabsContent value="categories" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Categories ({categories.length})</h3>
            <Button onClick={() => setShowCategoryForm(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add Category
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {categories.map((category) => (
              <Card key={category.id}>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      {category.name}
                    </CardTitle>
                    <Badge variant={category.is_active ? "default" : "secondary"}>
                      {category.is_active ? "Active" : "Inactive"}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setEditingCategory(category);
                        setShowCategoryForm(true);
                      }}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => toggleCategoryStatus(category.id, category.is_active)}
                    >
                      {category.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => deleteCategory(category.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="items" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">World Items ({filteredItems.length})</h3>
            <div className="flex gap-4 items-center">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium">Filter by Category:</label>
                <Select value={selectedCategoryFilter} onValueChange={setSelectedCategoryFilter}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {categories.map((category) => (
                      <SelectItem key={category.id} value={category.id}>
                        {category.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={() => setShowItemForm(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Add Item
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredItems.map((item) => {
              const category = categories.find(c => c.id === item.category_id);
              return (
                <Card key={item.id}>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg">{item.name}</CardTitle>
                      <Badge variant={item.is_active ? "default" : "secondary"}>
                        {item.is_active ? "Active" : "Inactive"}
                      </Badge>
                    </div>
                    <div className="text-sm text-gray-600">
                      Category: {category?.name}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <img
                      src={item.image_url}
                      alt={item.name}
                      className="w-full h-32 object-cover rounded-md mb-3"
                    />
                    <div
                      className="text-sm text-gray-600 mb-3 line-clamp-2"
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(item.description || "") }}
                    />
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEditItem(item.id)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => toggleItemStatus(item.id, item.is_active)}
                      >
                        {item.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => deleteItem(item.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>

      {showCategoryForm && (
        <WorldCategoryForm
          category={editingCategory}
          onSubmit={handleCategorySubmit}
          onCancel={() => {
            setShowCategoryForm(false);
            setEditingCategory(null);
          }}
        />
      )}

      {showItemForm && (
        <WorldItemForm
          item={editingItem}
          categories={categories}
          onClose={() => {
            setShowItemForm(false);
            setEditingItem(null);
          }}
          onSuccess={() => {
            fetchData();
            setShowItemForm(false);
            setEditingItem(null);
          }}
        />
      )}
    </div>
  );
};

export default WorldContentManager;
